import { useState } from 'react';
import { Button, FormField, Input, Modal, ModalBody, ModalFooter, ModalHeader, useToast } from '@vd/ui';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { useGuardedNavigate } from '../../app/NavigationGuard';
import { PROJECT_COLORS } from '@vd/tokens';
import { WorkspaceMark } from '../workspaces/WorkspaceMark';

/** The longest workspace name the server takes (`POST /companies`). */
const MAX_NAME = 80;

/**
 * A new workspace, for another business (TOADO-819): its own projects, team
 * and plan. The person who makes it is its owner, and it starts on the Free
 * plan, upgraded from its own Billing like any other. Made, it becomes the
 * current workspace and its home page opens.
 */
export function CreateWorkspaceModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { refreshUser, setCurrentCompany } = useAuth();
  const navigate = useGuardedNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const trimmed = name.trim();

  const close = () => {
    if (busy) return;
    setName('');
    onClose();
  };

  const create = async () => {
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const created = await api.createCompany({ name: trimmed });
      await refreshUser();
      setCurrentCompany(created.id);
      toast.push({ variant: 'success', title: `Created ${created.name}`, description: 'You are its owner. Add a project to start.' });
      setName('');
      onClose();
      navigate('/');
    } catch (error) {
      toast.push({ variant: 'danger', title: 'Could not create the workspace', description: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={close} width={460}>
      <ModalHeader>Create a new workspace</ModalHeader>
      <ModalBody>
        <form id="create-workspace" onSubmit={(event) => { event.preventDefault(); void create(); }}>
          <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: 13 }}>
            A workspace is one business: its own projects, team and plan. You are its owner, and it starts on the
            Free plan, which you can upgrade from its Billing settings.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }} data-testid="workspace-preview">
            {/* A preview of its mark as the name is typed; its colour is chosen when it is made (TOADO-906). */}
            <WorkspaceMark company={{ id: 'new', name: trimmed || 'New workspace', color: PROJECT_COLORS[0] }} size={32} />
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{trimmed || 'New workspace'}</span>
          </div>
          <FormField label="Workspace name">
            <Input
              autoFocus
              value={name}
              maxLength={MAX_NAME}
              onChange={(event) => setName(event.target.value)}
              placeholder="Acme Inc."
            />
          </FormField>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="primary" type="submit" form="create-workspace" disabled={!trimmed} loading={busy}>
          Create workspace
        </Button>
      </ModalFooter>
    </Modal>
  );
}
